import {
  ReservationQuestionDto,
  ReservationQuestionOptionDto,
} from "@/api/generated/generatedTypes";
import { EventQuestionType } from "@/api/reservation/dto";
import { QuestionnaireUILayout } from "@/components/shared/QuestionnaireUILayout";
import { EventReservationFormValues } from "@/pages/EventReservation.page";
import { Center, Text } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { useTimeout } from "@mantine/hooks";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { EventQuestionAndAnswerResolver } from "../EventQuestionAndAnswerResolver";
import { calculateProgressValue } from "../progressUtils";
import { getPathForReservationQuestion } from "../utils";

interface DateLocationQuestionnaireProps {
  eventQuestions: ReservationQuestionDto[];
  form: UseFormReturnType<
    EventReservationFormValues,
    (values: EventReservationFormValues) => EventReservationFormValues
  >;
  onQuestionnaireComplete: () => void;
  totalSteps: number;
  stepOffset: number;
}

export function DateLocationQuestionnaire({
  eventQuestions,
  form,
  onQuestionnaireComplete,
  totalSteps,
  stepOffset,
}: DateLocationQuestionnaireProps) {
  const [currentStep, setCurrentStep] = useState(0);

  const maxSteps = eventQuestions.length;
  const isLastStep = currentStep === maxSteps - 1;
  const currentQuestion = eventQuestions[currentStep];
  const progressValue = calculateProgressValue(currentStep, stepOffset, totalSteps);
  const navigate = useNavigate();

  const goToNextStep = () => {
    if (!isLastStep) {
      setCurrentStep((s) => s + 1);
    }
  };

  const { start: goToNextStepAfterDelay, clear: clearGoToNextStepDelay } = useTimeout(
    goToNextStep,
    300
  );

  const goToPreviousStep = () => {
    clearGoToNextStepDelay();
    if (currentStep === 0) {
      navigate("/home");
    }
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    }
  };

  const handleSelectSingle = (option: ReservationQuestionOptionDto) => {
    if (currentQuestion) {
      const path = getPathForReservationQuestion(currentQuestion);
      if (path) {
        form.setFieldValue(path as any, option.value);
      }

      if (currentQuestion.type === EventQuestionType.SELECT_SINGLE) {
        goToNextStepAfterDelay();
      }
    }
  };

  const handleSelectMultiple = (option: ReservationQuestionOptionDto) => {
    if (currentQuestion && currentQuestion.type === EventQuestionType.SELECT_MULTIPLE) {
      const path = getPathForReservationQuestion(currentQuestion);
      const currentVal = (form.getInputProps(path as any).value as string[] | null) || [];
      const isRemoving = currentVal.includes(option.value);
      const newSelection = isRemoving
        ? currentVal.filter((val) => val !== option.value)
        : [...currentVal, option.value];
      form.setFieldValue(path as any, newSelection);
    }
  };

  const getIsNextButtonDisabled = (): boolean => {
    if (!currentQuestion) return true;
    const path = getPathForReservationQuestion(currentQuestion);
    const value = form.getInputProps(path as any).value;

    if (currentQuestion.type === EventQuestionType.SELECT_MULTIPLE) {
      return !value || (value as string[]).length === 0;
    }

    return false;
  };

  if (!currentQuestion) {
    return (
      <Center h="100%">
        <Text>Error: Question not found for current step.</Text>
      </Center>
    );
  }

  const showNextButton = !isLastStep && currentQuestion.type === EventQuestionType.SELECT_MULTIPLE;

  const questionArea = (
    <EventQuestionAndAnswerResolver
      currentQuestion={currentQuestion}
      form={form}
      onSelectSingle={handleSelectSingle}
      onSelectMultiple={handleSelectMultiple}
    />
  );

  return (
    <QuestionnaireUILayout
      progressValue={progressValue}
      showBackButton
      onBackButtonClick={goToPreviousStep}
      title={currentQuestion.title}
      subtitle={currentQuestion.subtitle}
      questionAreaContent={questionArea}
      showNextButton={showNextButton}
      onNextButtonClick={goToNextStep}
      isNextButtonDisabled={getIsNextButtonDisabled()}
      isNextButtonLoading={false}
      showSubmitButton={isLastStep}
      onSubmitButtonClick={onQuestionnaireComplete}
      isSubmitButtonDisabled={getIsNextButtonDisabled()}
      isErrorState={false}
      pbForQuestionTitle={Boolean(currentQuestion?.options?.length ?? 0 > 6) ? "sm" : "xl"}
      submitButtonText="다음"
    />
  );
}
